import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Mail, Send } from 'lucide-react';

interface Subscriber {
  id: string;
  email: string;
  is_active: boolean;
  subscribed_at: string;
}

interface SubscribersSectionProps {
  subscribers: Subscriber[];
  onSendToSelected?: (selectedEmails: string[]) => void;
}

export const SubscribersSection = ({ subscribers, onSendToSelected }: SubscribersSectionProps) => {
  const [selectedSubscribers, setSelectedSubscribers] = useState<string[]>([]);

  const exportSubscribers = () => {
    const csvContent = [
      'Email,Status,Subscribed Date',
      ...subscribers.map(sub => 
        `${sub.email},${sub.is_active ? 'Active' : 'Inactive'},${new Date(sub.subscribed_at).toLocaleDateString()}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'newsletter_subscribers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hy-AM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const activeEmails = subscribers
        .filter(sub => sub.is_active)
        .map(sub => sub.email);
      setSelectedSubscribers(activeEmails);
    } else {
      setSelectedSubscribers([]);
    }
  };

  const handleSelectSubscriber = (email: string, checked: boolean) => {
    if (checked) {
      setSelectedSubscribers(prev => [...prev, email]);
    } else {
      setSelectedSubscribers(prev => prev.filter(e => e !== email));
    }
  };

  const handleSendToSelected = () => {
    if (onSendToSelected && selectedSubscribers.length > 0) {
      onSendToSelected(selectedSubscribers);
    }
  };

  return (
    <Card className="border-border/70 bg-card/80">
      <CardHeader>
        <CardTitle className="text-muted-foreground flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Newsletter բաժանորդներ
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Newsletter բաժանորդների ցանկ և կառավարում
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <Badge variant="secondary" className="bg-primary/20 text-muted-foreground">
              {subscribers.length} ընդամենը
            </Badge>
            <Badge variant="secondary" className="bg-blue-600/15 text-blue-600 dark:text-blue-400">
              {subscribers.filter(sub => sub.is_active).length} ակտիվ
            </Badge>
            {selectedSubscribers.length > 0 && (
              <Badge variant="secondary" className="bg-orange-600/15 text-orange-600 dark:text-orange-400">
                {selectedSubscribers.length} ընտրված
              </Badge>
            )}
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            {selectedSubscribers.length > 0 && onSendToSelected && (
              <Button
                onClick={handleSendToSelected}
                className="w-full bg-orange-600 hover:bg-orange-700 text-primary-foreground sm:w-auto"
              >
                <Send className="h-4 w-4 mr-2" />
                Ուղարկել ընտրվածներին
              </Button>
            )}
            <Button
              onClick={exportSubscribers}
              variant="outline"
              className="w-full border-border/70 text-muted-foreground hover:bg-primary/10 sm:w-auto"
            >
              <Download className="h-4 w-4 mr-2" />
              Արտահանել CSV
            </Button>
          </div>
        </div>

        {subscribers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Բաժանորդներ դեռ չկան
          </div>
        ) : (
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow className="border-border/70">
                  <TableHead className="text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedSubscribers.length === subscribers.filter(sub => sub.is_active).length && subscribers.filter(sub => sub.is_active).length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                      Ընտրել բոլորը
                    </div>
                  </TableHead>
                  <TableHead className="text-muted-foreground">Էլ. փոստ</TableHead>
                  <TableHead className="text-muted-foreground">Կարգավիճակ</TableHead>
                  <TableHead className="text-muted-foreground">Բաժանորդագրման ամսաթիվ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscribers.map((subscriber) => (
                  <TableRow key={subscriber.id} className="border-border/70">
                    <TableCell>
                      <Checkbox
                        checked={selectedSubscribers.includes(subscriber.email)}
                        onCheckedChange={(checked) => handleSelectSubscriber(subscriber.email, checked as boolean)}
                        disabled={!subscriber.is_active}
                      />
                    </TableCell>
                    <TableCell className="text-foreground">
                      {subscriber.email}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={subscriber.is_active ? "default" : "secondary"}
                        className={subscriber.is_active ? "bg-primary" : "bg-muted"}
                      >
                        {subscriber.is_active ? "Ակտիվ" : "Անակտիվ"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(subscriber.subscribed_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Mobile layout */}
        <div className="md:hidden space-y-4">
          {subscribers.map((subscriber) => (
            <div
              key={subscriber.id}
              className="flex items-center justify-between p-4 bg-muted rounded-lg border border-border/70"
            >
              <div className="flex items-center gap-3 flex-1">
                <Checkbox
                  checked={selectedSubscribers.includes(subscriber.email)}
                  onCheckedChange={(checked) => handleSelectSubscriber(subscriber.email, checked as boolean)}
                  disabled={!subscriber.is_active}
                />
                <div className="flex-1">
                  <p className="text-foreground text-sm mb-1">
                    {subscriber.email}
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={subscriber.is_active ? "default" : "secondary"}
                      className={subscriber.is_active ? "bg-primary text-xs" : "bg-muted text-xs"}
                    >
                      {subscriber.is_active ? "Ակտիվ" : "Անակտիվ"}
                    </Badge>
                    <span className="text-muted-foreground text-xs">
                      {formatDate(subscriber.subscribed_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};